import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAppDispatch, useAppSelector } from './store';
import { checkAuth } from './store/authSlice';
import GlobalSnackbar from './components/GlobalSnackbar';
import { appConfig } from './config/app';
import { useAuth } from './hooks/useAuth';
import { useInitialLoad } from './hooks/useInitialLoad';
import Login from './pages/Login';
import Callback from './pages/Callback';
import Dashboard from './pages/Dashboard';
import UserRoles from './pages/UserRoles';
import Users from './pages/Users';
import Storage from './pages/Storage';
import CustomFields from './pages/CustomFields';
import EmployeeTags from './pages/EmployeeTags';
import Shifts from './pages/Shifts';
import ShiftGroups from './pages/ShiftGroups';
import ShiftDistributions from './pages/ShiftDistributions';
import ApplicationSettings from './pages/ApplicationSettings';
import GlobalSettings from './pages/GlobalSettings';
import CalculatedValuesSettings from './pages/CalculatedValuesSettings';
import Employees from './pages/Employees';
import SystemInfo from './pages/SystemInfo';
import MaintenanceSystemStates from './pages/MaintenanceSystemStates';
import MaintenanceImport from './pages/MaintenanceImport';
import LeaveTypes from './pages/LeaveTypes';
import LeavePolicies from './pages/LeavePolicies';
import LeaveBalance from './pages/LeaveBalance';
import LeaveTransactions from './pages/LeaveTransactions';
import PublicHolidays from './pages/PublicHolidays';
import AccessDenied from './pages/AccessDenied';
import NotAuthorized from './pages/NotAuthorized';
import ParserProfiles from './pages/ParserProfiles';
import TimekeepingImport from './pages/TimekeepingImport';
import ManualTimekeepingEntry from './pages/ManualTimekeepingEntry';
import ApiKeysPage from './pages/ApiKeysPage';
import UserFieldAccess from './pages/UserFieldAccess';
import TimekeepingErrorCorrection from './pages/TimekeepingErrorCorrection';
import DataAutopsy from './pages/DataAutopsy';
import './App.css';

// Create a Material UI theme matching Mantis style
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid',
          borderColor: '#e0e0e0',
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, sessionStatus } = useAuth();
  const { initialLoadTotal, initialLoadCompleted } = useAppSelector((s) => s.auth);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Session exists but user has no valid unit selection — send back to login to re-authenticate
  if (sessionStatus === 'unselected') {
    return <Navigate to="/login" replace />;
  }

  // Session exists but the assigned unit is disabled or not authorized
  if (sessionStatus !== 'Allowed') {
    return <Navigate to="/not-authorized" replace />;
  }

  // Block page render until all initial data has loaded
  if (initialLoadTotal > 0 && initialLoadCompleted < initialLoadTotal) {
    const progress = Math.round((initialLoadCompleted / initialLoadTotal) * 100);
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" gap={2}>
        <Typography variant="h6" color="text.secondary" fontWeight={500}>
          Loading your workspace…
        </Typography>
        <Box sx={{ width: 280 }}>
          <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1, height: 6 }} />
        </Box>
        <Typography variant="body2" color="text.secondary">{progress}%</Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

// Permission-gated Route Component — must be used inside a ProtectedRoute
const PermissionRoute: React.FC<{ permission: string; children: React.ReactNode }> = ({
  permission,
  children,
}) => {
  const { permissions } = useAuth();

  return permissions.includes(permission) ? <>{children}</> : <AccessDenied />;
};

function App() {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(checkAuth(false));
  }, [dispatch]);

  useInitialLoad();

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (link) {
      link.href = appConfig.iconPath;
      link.type = 'image/svg+xml';
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CssBaseline />
      <GlobalSnackbar />
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<Callback />} />
            <Route path="/not-authorized" element={<NotAuthorized />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/system/roles"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_ROLE_PAGE">
                    <UserRoles />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/system/users"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_USER_PAGE">
                    <Users />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/system/user-field-access"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_USER_FIELD_DENIAL_NAVIGATION_MENU">
                    <UserFieldAccess />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/storage"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_STORAGE_PAGE">
                    <Storage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/system/custom-fields"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_CUSTOM_FIELD_PAGE">
                    <CustomFields />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-tags"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_EMPLOYEE_TAG_PAGE">
                    <EmployeeTags />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shifts"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_SHIFT_PAGE">
                    <Shifts />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shift-groups"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_SHIFT_GROUP_PAGE">
                    <ShiftGroups />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shift-distributions"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_SHIFT_DISTRIBUTION_PAGE">
                    <ShiftDistributions />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/system/employee-entry-settings"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_APPLICATION_SETTING_PAGE">
                    <ApplicationSettings />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/system/global-settings"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_APPLICATION_SETTING_PAGE">
                    <GlobalSettings />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/system/calculated-values"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_APPLICATION_SETTING_PAGE">
                    <CalculatedValuesSettings />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_EMPLOYEE_PAGE">
                    <Employees />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/system-info"
              element={
                <ProtectedRoute>
                  <SystemInfo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/maintenance/system-states"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_MAINTENANCE_PAGE">
                    <MaintenanceSystemStates />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/maintenance/import"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_MAINTENANCE_PAGE">
                    <MaintenanceImport />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leave-types"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_LEAVE_PAGE">
                    <LeaveTypes />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leave-policies"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_LEAVE_PAGE">
                    <LeavePolicies />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leave-balance"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_LEAVE_PAGE">
                    <LeaveBalance />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/leave-transactions"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_LEAVE_PAGE">
                    <LeaveTransactions />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/public-holidays"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_LEAVE_PAGE">
                    <PublicHolidays />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/timekeeping/parser-profiles"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_TIMEKEEPING_PAGE">
                    <ParserProfiles />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/timekeeping/import"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_TIMEKEEPING_PAGE">
                    <TimekeepingImport />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/timekeeping/manual-entry"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_MANUAL_TIMEKEEPING_PAGE">
                    <ManualTimekeepingEntry />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/timekeeping/error-correction"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_TIMEKEEPING_ERROR_PAGE">
                    <TimekeepingErrorCorrection />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/timekeeping/data-autopsy"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_TIMEKEEPING_ERROR_PAGE">
                    <DataAutopsy />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/timekeeping/api-keys"
              element={
                <ProtectedRoute>
                  <PermissionRoute permission="VIEW_API_KEY_PAGE">
                    <ApiKeysPage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
